import React, { useEffect, useState, useCallback } from "react";
import { Button, Input, Modal, Spin, Switch, Typography, Upload } from "antd";
import {
  AlertOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { clearEmpresaConfigCache } from "../../components/utils/empresaConfig";
import { apiGeneralInstance, authHeaderGeneral } from "../../redux/actions/general/general";
import { PATH } from "../../redux/utils";

import "./ConfiguracionPage.css";

const { Title, Text } = Typography;

const CATALOGOS = [
  {
    key: "avisos",
    titulo: "Avisos de inicio",
    desc: "Mensajes que se muestran en la pantalla de inicio para todos los usuarios",
    icon: AlertOutlined,
    placeholder: "Ej. Cerrado el 1 de enero",
    vacio: "Sin avisos configurados",
    maxLength: 255,
    lista: true,
  },
  {
    key: "ciudades",
    titulo: "Ciudades",
    desc: "Ciudades disponibles para el negocio",
    icon: EnvironmentOutlined,
    placeholder: "Nombre de la ciudad...",
    vacio: "Sin ciudades registradas",
    maxLength: 100,
  },
  {
    key: "divisas",
    titulo: "Divisas",
    desc: "Monedas aceptadas además del peso",
    icon: DollarOutlined,
    placeholder: "Ej. USD",
    vacio: "Sin divisas registradas",
    maxLength: 100,
  },
];

const logoSrc = (path) => (path ? `${PATH}${path}` : null);

function CatalogoBlock({ cfg }) {
  const Icono = cfg.icon;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGeneralInstance.get(`/general/${cfg.key}`, { headers: authHeaderGeneral() })
      .then((res) => setItems(res.data?.items ?? []))
      .catch(() => toast.error(`No se pudo cargar ${cfg.titulo.toLowerCase()}`))
      .finally(() => setLoading(false));
  }, [cfg.key, cfg.titulo]);

  const handleAdd = async () => {
    const valor = texto.trim();
    if (!valor) return;
    setSaving(true);
    try {
      const res = await apiGeneralInstance.post(`/general/${cfg.key}`, { texto: valor }, { headers: authHeaderGeneral() });
      setItems((prev) => [...prev, res.data?.item ?? res.data]);
      setTexto("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (it) => {
    Modal.confirm({
      title: "Eliminar registro",
      content: `¿Seguro que deseas eliminar "${it.texto}"?`,
      okText: "Eliminar",
      cancelText: "Cancelar",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiGeneralInstance.delete(`/general/${cfg.key}/${it.id}`, { headers: authHeaderGeneral() });
          setItems((prev) => prev.filter((x) => x.id !== it.id));
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        }
      },
    });
  };

  const botonEliminar = (it) => (
    <button type="button" className="cfg-tipo-chip-del" onClick={() => handleDelete(it)} title="Eliminar">
      <i className="fa-solid fa-trash" />
    </button>
  );

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <Icono className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">{cfg.titulo}</div>
          <div className="cfg-block-desc">{cfg.desc}</div>
        </div>
      </div>

      <div className="cfg-tipo-add-row">
        <Input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={cfg.placeholder}
          className="cfg-tipo-input"
          onPressEnter={handleAdd}
          maxLength={cfg.maxLength}
        />
        <Button
          icon={<i className="fa-solid fa-plus" />}
          onClick={handleAdd}
          loading={saving}
          disabled={!texto.trim()}
          className="cfg-tipo-btn-add"
        >
          Agregar
        </Button>
      </div>

      {loading ? (
        <div className="cfg-loading-row"><Spin size="small" /></div>
      ) : items.length === 0 ? (
        <div className="cfg-tipo-empty">{cfg.vacio}</div>
      ) : cfg.lista ? (
        <div className="cfg-aviso-list">
          {items.map((it) => (
            <div key={it.id} className="cfg-aviso-item">
              <span className="cfg-aviso-item-text">{it.texto}</span>
              {botonEliminar(it)}
            </div>
          ))}
        </div>
      ) : (
        <div className="cfg-tipo-chips">
          {items.map((it) => (
            <div key={it.id} className="cfg-tipo-chip">
              <span className="cfg-tipo-chip-label">{it.texto}</span>
              {botonEliminar(it)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmpresaBlock() {
  const [empresa, setEmpresa] = useState({ nombre_empresa: "", path: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editando, setEditando] = useState(false);
  const [nombreDraft, setNombreDraft] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchEmpresa = useCallback(() => {
    apiGeneralInstance.get("/general/empresa", { headers: authHeaderGeneral() })
      .then((res) => setEmpresa({ nombre_empresa: res.data?.nombre_empresa || "", path: res.data?.path || null }))
      .catch(() => toast.error("No se pudo cargar la empresa"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEmpresa(); }, [fetchEmpresa]);

  const handleEditar = () => {
    setNombreDraft(empresa.nombre_empresa || "");
    setLogoFile(null);
    setLogoPreview(null);
    setEditando(true);
  };

  const handleCancelar = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
    setEditando(false);
  };

  const handleLogoChange = (file) => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    return false;
  };

  const handleGuardar = async () => {
    if (!nombreDraft.trim()) return toast.warning("El nombre es obligatorio");
    setSaving(true);
    try {
      await apiGeneralInstance.put("/general/empresa", { nombre_empresa: nombreDraft.trim() }, { headers: authHeaderGeneral() });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await apiGeneralInstance.post("/general/empresa/logo", fd, {
          headers: { ...authHeaderGeneral(), "Content-Type": "multipart/form-data" },
        });
      }
      clearEmpresaConfigCache();
      toast.success("Datos de la empresa guardados");
      handleCancelar();
      fetchEmpresa();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <ShopOutlined className="cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Datos de la empresa</div>
          <div className="cfg-block-desc">Nombre y logo usados en reportes PDF de todos los módulos</div>
        </div>
      </div>

      {loading ? (
        <div className="cfg-loading-row"><Spin size="small" /></div>
      ) : !editando ? (
        <div className="cfg-empresa-card">
          <div className={`cfg-empresa-logo-zone cfg-empresa-logo-zone--view${empresa.path ? " cfg-empresa-logo-zone--filled" : ""}`}>
            {empresa.path ? (
              <img src={logoSrc(empresa.path)} alt="Logo empresa" className="cfg-empresa-logo-img" />
            ) : (
              <div className="cfg-empresa-logo-empty">
                <ShopOutlined className="cfg-empresa-logo-empty-icon" />
                <span>Sin logo</span>
              </div>
            )}
          </div>
          <div className="cfg-empresa-fields">
            <div className="cfg-empresa-view-nombre">
              {empresa.nombre_empresa || <span className="cfg-empresa-view-vacio">Sin nombre configurado</span>}
            </div>
            <button type="button" className="cfg-empresa-edit-btn" onClick={handleEditar}>
              <i className="fa-solid fa-pen" />
              Editar
            </button>
          </div>
        </div>
      ) : (
        <div className="cfg-empresa-card">
          <Upload accept="image/*" showUploadList={false} beforeUpload={handleLogoChange}>
            <div className={`cfg-empresa-logo-zone${(logoPreview || empresa.path) ? " cfg-empresa-logo-zone--filled" : ""}`}>
              {(logoPreview || empresa.path) ? (
                <img src={logoPreview || logoSrc(empresa.path)} alt="Logo empresa" className="cfg-empresa-logo-img" />
              ) : (
                <div className="cfg-empresa-logo-empty">
                  <UploadOutlined className="cfg-empresa-logo-empty-icon" />
                  <span>Subir logo</span>
                  <span className="cfg-empresa-logo-empty-hint">PNG · JPG · WEBP</span>
                </div>
              )}
              <div className="cfg-empresa-logo-overlay">
                <UploadOutlined />
                <span>{empresa.path ? "Cambiar" : "Subir"}</span>
              </div>
            </div>
          </Upload>

          <div className="cfg-empresa-fields">
            <div className="cfg-empresa-field">
              <label className="cfg-empresa-label">Nombre de la empresa</label>
              <Input
                value={nombreDraft}
                onChange={(e) => setNombreDraft(e.target.value)}
                placeholder="Nombre de la empresa"
                className="cfg-empresa-input"
                maxLength={150}
                size="large"
              />
            </div>
            {logoFile && (
              <div className="cfg-empresa-file-badge">
                <UploadOutlined style={{ fontSize: 12 }} />
                {logoFile.name}
              </div>
            )}
            <div className="cfg-empresa-actions">
              <Button onClick={handleGuardar} loading={saving} className="cfg-tipo-btn-add">
                Guardar cambios
              </Button>
              <button type="button" className="cfg-empresa-cancel-btn" onClick={handleCancelar}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PosBlock() {
  const [permitir, setPermitir] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGeneralInstance.get("/general/pos-config", { headers: authHeaderGeneral() })
      .then((res) => setPermitir(res.data?.permitir_venta_sin_existencia !== false))
      .catch(() => toast.error("No se pudo cargar la configuración del punto de venta"))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = async (value) => {
    setPermitir(value);
    try {
      await apiGeneralInstance.put("/general/pos-config", { permitir_venta_sin_existencia: value }, { headers: authHeaderGeneral() });
      toast.success(value ? "Se podrá vender sin existencia" : "Venta sin existencia bloqueada");
    } catch (err) {
      setPermitir(!value);
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    }
  };

  return (
    <div className="cfg-block">
      <div className="cfg-block-header">
        <i className="fa-solid fa-cash-register cfg-block-icon" />
        <div>
          <div className="cfg-block-title">Punto de venta</div>
          <div className="cfg-block-desc">Reglas de cobro para el cajero</div>
        </div>
      </div>

      {loading ? (
        <div className="cfg-loading-row"><Spin size="small" /></div>
      ) : (
        <div className="cfg-correo-list">
          <div className="cfg-correo-row">
            <div className="cfg-correo-row-info">
              <span className="cfg-correo-row-label">Permitir vender sin existencia</span>
              <span className="cfg-correo-row-desc">
                El cajero solo recibe un aviso y la línea queda marcada en la venta para cuadrar el inventario. Si lo apagas, se bloquea la venta.
              </span>
            </div>
            <Switch checked={permitir} onChange={handleChange} />
          </div>
        </div>
      )}
    </div>
  );
}

function PanelGeneral() {
  return (
    <div className="cfg-panel-body">
      <EmpresaBlock />
      <div className="cfg-divider" style={{ marginBottom: 50 }} />
      <PosBlock />
      {CATALOGOS.map((c) => (
        <React.Fragment key={c.key}>
          <div className="cfg-divider" style={{ marginBottom: 50 }} />
          <CatalogoBlock cfg={c} />
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Módulos del sidebar ────────────────────────────────────────────────────

const MODULES = [
  {
    key: "general",
    label: "General",
    icon: <i className="fa-solid fa-gear" />,
    color: "#374151",
    bg: "linear-gradient(140deg, #374151 0%, #9ca3af 100%)",
  },
];

const PANELS = {
  general: PanelGeneral,
};

// ── Página ─────────────────────────────────────────────────────────────────

export default function ConfiguracionPage() {
  const [active, setActive] = useState("general");
  const activeModule = MODULES.find((m) => m.key === active);

  return (
    <main className="cfg-main">
      <div className="cfg-layout">

        <aside className="cfg-sidebar">
          <div className="cfg-sidebar-header">
            <Title level={5} className="cfg-sidebar-title">Configuración</Title>
          </div>

          <nav className="cfg-nav">
            {MODULES.map((mod) => (
              <button
                key={mod.key}
                type="button"
                className={`cfg-nav-item${active === mod.key ? " cfg-nav-item--active" : ""}`}
                onClick={() => setActive(mod.key)}
                style={active === mod.key ? { "--mod-color": mod.color } : {}}
              >
                <span
                  className="cfg-nav-icon"
                  style={{ background: active === mod.key ? mod.bg : undefined }}
                >
                  {mod.icon}
                </span>
                <span className="cfg-nav-label">{mod.label}</span>
                {active === mod.key && <span className="cfg-nav-active-bar" />}
              </button>
            ))}
          </nav>
        </aside>

        <section className="cfg-content">
          <div className="cfg-content-card">
            <div className="cfg-content-header">
              <span className="cfg-content-icon" style={{ background: activeModule?.bg }}>
                {activeModule?.icon}
              </span>
              <div>
                <Title level={3} className="cfg-content-title">{activeModule?.label}</Title>
                <Text type="secondary" className="cfg-content-subtitle">Configuración del módulo</Text>
              </div>
            </div>
            <div className="cfg-content-inner-divider" />
            {React.createElement(PANELS[active] || PanelGeneral)}
          </div>
        </section>

      </div>
    </main>
  );
}
